import { useEffect, useState } from 'react';
import { AlertIcon, ArrowUpRightIcon, DownloadIcon, FileTextIcon, RefreshIcon, SheetIcon } from '../icons';
import { formatBytes } from './files';
import { Button } from '../Button';

// Les fichiers générés sont supprimés du serveur après 24 h (GENERATED_FILES_TTL_HOURS)
const EXPIRED = 'Ce fichier n’est plus disponible : les fichiers générés sont conservés 24 h. Redemandez-le à l’assistant.';

/**
 * Visionneuse « Fichier » :
 * - PDF : affiché dans une iframe à partir d'un blob (le serveur interdit l'affichage
 *   direct en cadre : X-Frame-Options DENY) ;
 * - autres types : informations + téléchargement (visionneuses riches au sprint 5).
 */
export function FileArtifact({ artifact }) {
  const { url, name, ext, type } = artifact.payload ?? {};
  const isPdf = ext === 'pdf';
  const [state, setState] = useState({ status: 'loading', blobUrl: null, size: null, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!url) return undefined;
    const controller = new AbortController();
    let blobUrl = null;
    setState({ status: 'loading', blobUrl: null, size: null, error: null });

    (async () => {
      try {
        const response = await fetch(url, { method: isPdf ? 'GET' : 'HEAD', signal: controller.signal, cache: 'no-store' });
        if (response.status === 404 || response.status === 410) throw Object.assign(new Error(EXPIRED), { expired: true });
        if (!response.ok) throw new Error(`Aperçu impossible (HTTP ${response.status}).`);
        if (isPdf) {
          const data = await response.arrayBuffer();
          blobUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
          setState({ status: 'ready', blobUrl, size: data.byteLength, error: null });
        } else {
          const length = Number(response.headers.get('content-length'));
          setState({ status: 'ready', blobUrl: null, size: Number.isFinite(length) && length > 0 ? length : null, error: null });
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        setState({
          status: 'error',
          blobUrl: null,
          size: null,
          error: err.expired ? err.message : (err instanceof TypeError ? 'Serveur injoignable : vérifiez votre connexion.' : err.message),
        });
      }
    })();

    return () => {
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url, isPdf, attempt]);

  const Icon = ext === 'xlsx' || ext === 'csv' ? SheetIcon : FileTextIcon;
  const size = formatBytes(state.size);

  return (
    <div className="artifact-view artifact-file">
      <div className="artifact-toolbar" role="toolbar" aria-label="Actions sur le fichier">
        <span className="artifact-toolbar__meta">
          {type?.label ?? 'Fichier'}{size ? ` · ${size}` : ''}
        </span>
        <div className="artifact-toolbar__actions">
          {isPdf && state.blobUrl && (
            <Button as="a" variant="ghost" size="sm" icon={ArrowUpRightIcon} href={state.blobUrl} target="_blank" rel="noopener noreferrer">
              <span>Ouvrir dans un onglet</span>
            </Button>
          )}
          <Button as="a" variant="ghost" size="sm" icon={DownloadIcon} href={url} download={name}>
            <span>Télécharger</span>
          </Button>
        </div>
      </div>

      {state.status === 'loading' && (
        <div className="artifact-file__loading" role="status">
          <span className="artifact-skeleton" aria-hidden="true" />
          <span className="sr-only">Chargement de l'aperçu…</span>
        </div>
      )}

      {state.status === 'error' && (
        <div className="artifact-state artifact-state--error" role="alert">
          <AlertIcon size={18} />
          <p>{state.error}</p>
          {!state.error?.startsWith('Ce fichier') && (
            <Button variant="outline" icon={RefreshIcon} iconSize={14} onClick={() => setAttempt((n) => n + 1)}>
              Réessayer
            </Button>
          )}
        </div>
      )}

      {state.status === 'ready' && isPdf && state.blobUrl && (
        <iframe className="artifact-file__frame" src={state.blobUrl} title={`Aperçu de ${name}`} />
      )}

      {state.status === 'ready' && !isPdf && (
        <div className="artifact-file__card">
          <span className={`artifact-file__icon artifact-file__icon--${ext || 'other'}`} aria-hidden="true">
            <Icon size={28} />
          </span>
          <p className="artifact-file__name">{name}</p>
          <p className="artifact-file__meta">{[type?.label, size].filter(Boolean).join(' · ')}</p>
          <p className="artifact-file__note">
            L'aperçu détaillé de ce type de fichier arrive bientôt. Téléchargez-le pour l'ouvrir dans {type?.short ?? 'votre logiciel'}.
          </p>
          <Button as="a" variant="primary" icon={DownloadIcon} iconSize={15} href={url} download={name}>
            Télécharger
          </Button>
        </div>
      )}
    </div>
  );
}
